import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLoker } from '../services/api';
import { ArrowRight, Search } from 'lucide-react';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import usePageHeaders from '../hooks/usePageHeaders';

const SectionTag = ({ children }) => (
  <div className="flex items-center gap-2 mb-6">
    <div className="w-8 h-[2px] bg-blue-600"></div>
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-600 italic">
      {children}
    </span>
  </div>
);

const Karir = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // --- SEO & Schema Markup ---
  useEffect(() => {
    // 1. Title & Meta Tags
    document.title = "Karir - Lowongan Kerja Konveksi Bandung | iMoonusel";
    
    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta("description", "Temukan peluang karir di iMoonusel Konveksi. Kami membuka lowongan kerja untuk posisi penjahit, pattern maker, dan staf produksi di Bandung.");
    setMeta("keywords", "loker konveksi, lowongan kerja bandung, karir garment, loker penjahit, lowongan staff produksi");

    // Open Graph
    const setOg = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setOg("og:title", "Karir - Lowongan Kerja Konveksi Bandung | iMoonusel");
    setOg("og:description", "Bergabunglah dengan tim produksi garment terbaik di Bandung.");
    setOg("og:image", "/logo512.png");
    setOg("og:url", window.location.href);
    setOg("og:type", "website");
  }, []);

  const headers = usePageHeaders();  const navigate = useNavigate();
  const limit = 10;

  const fetchJobs = useCallback(async (pageNum, search = '') => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const response = await getLoker(pageNum, limit, search);
      
      if (response && response.data) {
        setJobs(prev => {
          if (pageNum === 1) return response.data;
          const newIds = new Set(response.data.map(a => a.id));
          return [...prev, ...response.data.filter(a => !newIds.has(a.id))];
        });
        
        if (response.data.length < limit || response.pagination.current_page >= response.pagination.total_pages) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } else {
        setError('Gagal memuat lowongan kerja.');
      }
    } catch (err) {
      setError('Terjadi kesalahan saat mengambil data.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs(1, searchQuery);
  }, [fetchJobs]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setPage(1);
      fetchJobs(1, searchQuery);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, fetchJobs]);

  const handleLoadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchJobs(nextPage, searchQuery);
    }
  }, [loading, loadingMore, hasMore, page, fetchJobs, searchQuery]);

  const lastJobRef = useInfiniteScroll(handleLoadMore, hasMore, loading || loadingMore);

  const formatDate = (dateString) => {
    if (!dateString) return 'OPEN';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-white text-black pt-24 pb-24">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-6 mb-12 md:mb-24">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-8">
                <SectionTag>Careers</SectionTag>
                <h1 className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-6 md:mb-8 uppercase">
                  Join The <br/><span className="text-zinc-300">Revolution.</span>
                </h1>
            </div>
            <div className="md:col-span-4 flex flex-col justify-end pb-2 md:pb-4">
                <p className="text-zinc-500 font-light leading-relaxed mb-8 text-sm md:text-base">
                  Kami mencari talenta terbaik untuk membangun masa depan industri manufaktur apparel. Jika Anda memiliki obsesi terhadap kualitas, mari bicara.
                </p>
                <div className="border-b border-black pb-2 flex items-center gap-3">
                    <Search size={18} className="text-zinc-400" />
                    <input 
                        type="text" 
                        placeholder="SEARCH POSITION..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-transparent border-none focus:outline-none text-sm font-bold uppercase tracking-widest placeholder-zinc-400"
                    />
                </div>
            </div>
        </div>
      </div>

      {/* Job List */}
      <div className="max-w-7xl mx-auto px-6">
        {/* Added specific margin-top to avoid double line gap look if they were close, but here they are separated by mb-12/24 above. 
            The border-t is on this container. */}
        <div className="border-t border-black">
          {loading && jobs.length === 0 ? (
             <div className="py-24 text-center">
                <div className="inline-block w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
             </div>
          ) : error ? (
             <div className="py-24 text-center text-red-600 font-bold">{error}</div>
          ) : jobs.length === 0 ? (
             <div className="py-24 text-center text-zinc-400 font-bold uppercase tracking-widest">
                Tidak ada lowongan tersedia saat ini.
             </div>
          ) : (
            jobs.map((job, index) => {
                const isLast = jobs.length === index + 1;
                return (
                    <div 
                        key={job.id} 
                        ref={isLast ? lastJobRef : null}
                        onClick={() => navigate(`/karir/${job.id}`)}
                        className="group border-b border-zinc-300 py-6 md:py-8 flex flex-col gap-4 md:grid md:grid-cols-12 md:items-center cursor-pointer hover:bg-zinc-50 transition-colors"
                    >
                        {/* Mobile Top Row: Image + Title + Mobile Arrow */}
                        <div className="flex flex-row items-start gap-4 w-full md:contents">
                            
                            {/* Image: Flex item on mobile, Grid col 1 on desktop */}
                            {job.cover_image && (
                                <div className="shrink-0 md:col-span-1">
                                    <img 
                                        src={job.cover_image} 
                                        alt="icon" 
                                        className="w-12 h-12 object-contain"
                                        onError={(e) => {e.target.style.display='none'}}
                                    />
                                </div>
                            )}

                            {/* Text: Flex grow on mobile, Grid col 4/5 on desktop */}
                            <div className={`flex-grow ${job.cover_image ? 'md:col-span-4' : 'md:col-span-5'}`}>
                                <h3 className="text-xl md:text-2xl font-bold tracking-tight group-hover:text-blue-600 transition-colors">
                                    {job.title}
                                </h3>
                                <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest mt-1">
                                    {job.position}
                                </p>
                            </div>

                            {/* Mobile Arrow: Flex item, Hidden on Desktop */}
                            <div className="md:hidden shrink-0 pt-1">
                                <ArrowRight size={20} />
                            </div>
                        </div>

                        {/* Mobile Bottom Row: Meta Info */}
                        <div className="flex items-center justify-between w-full md:contents text-zinc-500">
                             
                             {/* Left: Type & Location */}
                             <div className="flex items-center gap-3 md:contents">
                                 {/* Job Type */}
                                 <div className="md:col-span-3">
                                    <span className="text-xs md:text-sm font-medium text-zinc-600 border border-zinc-200 px-3 py-1 rounded-full whitespace-nowrap">
                                        {job.job_type || 'Full Time'}
                                    </span>
                                 </div>
                                 
                                 {/* Location */}
                                 <div className="md:col-span-2 text-xs md:text-sm font-medium flex items-center gap-1">
                                    Bandung, ID
                                 </div>
                             </div>

                             {/* Right: Date & Desktop Arrow */}
                             <div className="md:col-span-2 flex items-center justify-end md:justify-between w-auto">
                                <span className="text-xs font-bold uppercase tracking-widest">
                                    {formatDate(job.created_at)}
                                </span>
                                
                                {/* Desktop Arrow */}
                                <div className="hidden md:flex w-8 h-8 items-center justify-center bg-black text-white rounded-none opacity-0 group-hover:opacity-100 transition-all -translate-x-4 group-hover:translate-x-0">
                                    <ArrowRight size={16} />
                                </div>
                             </div>
                        </div>
                    </div>
                );
            })
          )}
          
          {loadingMore && (
              <div className="py-8 text-center text-xs font-bold uppercase tracking-widest text-zinc-400 animate-pulse">
                  Loading More...
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Karir;